import { useEffect, useState } from "react";

/**
 * Hook Tauri auto-updater.
 *
 * Strategie :
 *   - Au mount, check une fois (silencieux). Puis re-check toutes les 4 h.
 *   - Si une Update est dispo, expose son `version` + une fonction
 *     `installAndRestart()` qui DL le payload + relaunch.
 *   - Inerte hors Tauri (web browser, PWA mobile) ET tant que
 *     tauri.conf.json -> plugins.updater.active=false (l'API throw, on catch
 *     silencieusement).
 *
 * Le payload Tauri retourne un objet Update au type opaque (`unknown` ici) —
 * on lui parle uniquement via `.downloadAndInstall()`. Sa structure complete :
 *   { version, date, body, available, currentVersion, ... }
 * On ne lit que `version` + `body` (changelog).
 *
 * Le SHA Ed25519 du payload est verifie au niveau Rust avant l'install — pas
 * besoin de re-verifier cote JS. Si la verif rate, `downloadAndInstall()`
 * throw une erreur "signature mismatch".
 */

const CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000; // 4h

const IS_IN_TAURI =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

interface AvailableUpdate {
  version: string;
  body: string | null;
  install: () => Promise<void>;
}

export interface UseUpdaterResult {
  /** Une update est dispo et prete a etre installee. */
  available: AvailableUpdate | null;
  /** True pendant le check ET pendant le DL/install. */
  busy: boolean;
  /** Erreur du dernier check ou install (logged en console). */
  error: string | null;
  /** Force un check immediat (deja appele auto au mount + interval). */
  recheck: () => Promise<void>;
}

export function useUpdater(): UseUpdaterResult {
  const [available, setAvailable] = useState<AvailableUpdate | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doCheck = async () => {
    if (!IS_IN_TAURI) return;
    setBusy(true);
    setError(null);
    try {
      // Lazy import pour ne pas embarquer le plugin dans le bundle web.
      const mod = await import("@tauri-apps/plugin-updater");
      const update = await mod.check();
      if (update?.available) {
        setAvailable({
          version: update.version,
          body: update.body ?? null,
          install: async () => {
            setBusy(true);
            setError(null);
            try {
              await update.downloadAndInstall();
              // L'app va relaunch automatiquement apres l'install
              const relaunchMod = await import("@tauri-apps/plugin-process");
              await relaunchMod.relaunch();
            } catch (e) {
              const msg = e instanceof Error ? e.message : String(e);
              console.error("[updater] install failed:", msg);
              setError(msg);
              setBusy(false);
            }
          },
        });
      } else {
        setAvailable(null);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // active=false ou pubkey vide → check() throw. C'est attendu en V0, on
      // log en debug pas en error pour ne pas spammer la console.
      if (msg.includes("updater") || msg.includes("pubkey")) {
        console.debug("[updater] not configured yet:", msg);
      } else {
        console.error("[updater] check failed:", msg);
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!IS_IN_TAURI) return;
    void doCheck();
    const id = window.setInterval(() => void doCheck(), CHECK_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { available, busy, error, recheck: doCheck };
}
